<template>
  <Modal name="ADD_ITEM_SERVICE_VALUES" :title="$t('contract.actions.addServiceContract')">
    <template v-slot:body>
      <v-form ref="addServiceValuesForm" v-model="valid">
        <v-autocomplete
          v-model="type"
          :no-data-text="$t('contract.noData')"
          :label="$t('contract.label.serviceType')"
          :items="serviceTypes"
          :rules="[rules.required]"
          :item-text="(item) => $t(`type.${item.value}`)"
          item-value="value"
          hide-details="auto"
          hide-selected
          single-line
          clearable
          outlined
        />

        <h3 class="mt-8">
          {{ $t('contract.label.values') }}
        </h3>

        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.lazy="price"
              v-money="catalogCurrency()"
              :label="$t('contract.label.price')"
              :rules="[rules.required]"
              class="amount inputAmount mr-2"
              hide-details="auto"
              ref="price"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="discount"
              :label="$t('contract.label.discount')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model.lazy="square_meter_price"
              v-money="catalogCurrency()"
              :label="$t('contract.label.squareMetersPrice')"
              :rules="[rules.required]"
              class="amount inputAmount mr-2"
              hide-details="auto"
              ref="square_meter_price"
              type="tel"
              outlined
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="base_square_meters_size"
              :label="$t('contract.label.baseSquareMetersPrice')"
              :rules="[rules.required]"
              class="amount inputAmount mr-2"
              hide-details="auto"
              suffix="m²"
              outlined
            />
          </v-col>
        </v-row>

        <h3 class="mt-8">
          {{ $t('defaultServiceValues.taxes') }}
        </h3>

        <v-row>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="host_tax"
              :label="$t('contract.label.taxHost')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="displacement_tax"
              :label="$t('contract.label.displacementTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="square_meter_tax"
              :label="$t('contract.label.taxSquareMeters')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="furniture_tax"
              :label="$t('contract.label.furnitureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="semi_furniture_tax"
              :label="$t('contract.label.semiFurnitureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="house_tax"
              :label="$t('contract.label.houseTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="trousseau_tax"
              :label="$t('contract.label.trousseauTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              v-model="host_realty_feature_tax"
              :label="$t('contract.label.hostRealtyFeatureTax')"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              :label="$t('contract.label.urgencyTax')"
              v-model="urgency_tax"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
            />
          </v-col>
          <v-col cols="12" sm="6">
            <HAmount
              :label="$t('contract.label.hostUrgencyTax')"
              :amount="host_urgency_tax"
              v-model="host_urgency_tax"
              :minAmount="0"
              :maxAmount="100"
              fullWidth
              percent
              :disabled="urgency_tax <= 0"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="urgency_hours_limit"
              :label="$t('contract.label.urgencyHoursLimit')"
              class="amount inputAmount mr-2"
              :rules="urgency_tax > 0 ? [rules.required] : []"
              hide-details="auto"
              suffix="h"
              outlined
              :disabled="urgency_tax <= 0"
            />
          </v-col>
        </v-row>

        <v-spacer class="my-5" />

        <div class="d-flex justify-end">
          <v-btn
            @click="addService"
            :disabled="!valid"
            class="mw-150"
            color="primary"
            elevation="0"
            large
          >
            {{ $t('contract.actions.addService') }}
          </v-btn>
        </div>
      </v-form>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import { amount, percent, required } from '@/util/errorHandling'
import { catalogCurrency } from '@/util/catalog'
import { mapActions } from 'vuex'
import { VMoney } from 'v-money'
import HAmount from '../../app/HAmount.vue'

export default {
  name: 'AddServicesValues',

  components: {
    Modal,
    HAmount
  },

  props: {
    serviceTypes: {
      required: true
    }
  },

  directives: {
    money: VMoney
  },

  data() {
    return {
      type: null,
      price: null,
      discount: 0,
      square_meter_price: 0,
      base_square_meters_size: null,
      host_tax: 0,
      displacement_tax: 0,
      square_meter_tax: 0,
      furniture_tax: 0,
      semi_furniture_tax: 0,
      house_tax: 0,
      trousseau_tax: 0,
      host_realty_feature_tax: 0,
      urgency_tax: 0,
      host_urgency_tax: 0,
      urgency_hours_limit: null,

      rules: {
        amount,
        percent,
        required,
      },

      valid: false,

      catalogCurrency,
    }
  },

  watch: {
    urgency_tax(tax) {
      if (!tax || tax <= 0) {
        this.host_urgency_tax = 0
        this.urgency_hours_limit = null
      }
    }
  },

  methods: {
    ...mapActions(['closeModal']),

    addService() {
      let serviceContract = {
        type: this.type,
        price: this.price,
        discount: this.discount,
        square_meter_price: this.square_meter_price,
        base_square_meters_size: this.base_square_meters_size && this.base_square_meters_size.replace(',', '.'),
        host_tax: this.host_tax,
        displacement_tax: this.displacement_tax,
        square_meter_tax: this.square_meter_tax,
        furniture_tax: this.furniture_tax,
        semi_furniture_tax: this.semi_furniture_tax,
        house_tax: this.house_tax,
        trousseau_tax: this.trousseau_tax,
        host_realty_feature_tax: this.host_realty_feature_tax,
        urgency_tax: this.urgency_tax,
        host_urgency_tax: this.host_urgency_tax,
        urgency_hours_limit: this.urgency_hours_limit,
      }

      this.reset()
      this.$emit('newServiceCreated', serviceContract)
      this.closeModal('ADD_ITEM_SERVICE_VALUES')
    },

    reset() {
      this.type = null
      this.price = null
      this.discount = 0
      this.square_meter_price = 0
      this.base_square_meters_size = null
      this.host_tax = 0
      this.displacement_tax = 0
      this.square_meter_tax = 0
      this.furniture_tax = 0
      this.semi_furniture_tax = 0
      this.house_tax = 0
      this.trousseau_tax = 0
      this.host_realty_feature_tax = 0
      this.urgency_tax = 0
      this.host_urgency_tax = 0
      this.urgency_hours_limit = null

      this.resetValue()
    },

    resetValue() {
      this.price = 0

      const price = this.$refs?.price?.$refs?.input
      price.value = '0.00'
    }
  },
}
</script>
